import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api/baseQuery";
import { HTTP_STATUS } from "../api/constants";

// Ticket/Feedback types (from API: 1-based enum)
// Backend mapping: Suggestion = 1, Complaint = 2
export const TICKET_TYPES = {
  SUGGESTION: 1,
  COMPLAINT: 2,
};

// Ticket status (from API: 1-based enum)
// Backend mapping: New = 1, InReview = 2, Resolved = 3, Rejected = 4, Pending = 5
export const TICKET_STATUS = {
  NEW: 1,
  IN_REVIEW: 2,
  RESOLVED: 3,
  REJECTED: 4,
  PENDING: 5,
};

// Ticket priority (from API: 1-based enum)
// Backend mapping: Low = 1, Medium = 2, High = 3
export const TICKET_PRIORITY = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
};

// Ticket categories (from API: 1-based enum)
// Backend mapping: System = 1, Content = 2, Company = 3, Other = 4
export const TICKET_CATEGORIES = {
  SYSTEM: 1,
  CONTENT: 2,
  COMPANY: 3,
  OTHER: 4,
};

// Conversation response types (compatibility with previous implementation)
export const RESPONSE_TYPES = {
  ADMIN: 0,
  USER: 1,
  SYSTEM: 2,
};

// Helper functions for labels
export const getTicketTypeLabel = (type) => {
  const labels = {
    [TICKET_TYPES.SUGGESTION]: 'Suggestion',
    [TICKET_TYPES.COMPLAINT]: 'Complaint'
  };
  return labels[type] || 'Unknown';
};

export const getTicketStatusLabel = (status) => {
  const labels = {
    [TICKET_STATUS.NEW]: 'New',
    [TICKET_STATUS.IN_REVIEW]: 'In Review',
    [TICKET_STATUS.RESOLVED]: 'Resolved',
    [TICKET_STATUS.REJECTED]: 'Rejected',
    [TICKET_STATUS.PENDING]: 'Pending'
  };
  return labels[status] || 'Unknown';
};

export const getTicketPriorityLabel = (priority) => {
  const labels = {
    [TICKET_PRIORITY.LOW]: 'Low',
    [TICKET_PRIORITY.MEDIUM]: 'Medium',
    [TICKET_PRIORITY.HIGH]: 'High'
  };
  return labels[priority] || 'Unknown';
};

export const getTicketCategoryLabel = (category) => {
  const labels = {
    [TICKET_CATEGORIES.SYSTEM]: 'System',
    [TICKET_CATEGORIES.CONTENT]: 'Content',
    [TICKET_CATEGORIES.COMPANY]: 'Company',
    [TICKET_CATEGORIES.OTHER]: 'Other'
  };
  return labels[category] || 'Unknown';
};

export const getResponseTypeLabel = (type) => {
  const labels = {
    [RESPONSE_TYPES.ADMIN]: 'Admin',
    [RESPONSE_TYPES.USER]: 'User',
    [RESPONSE_TYPES.SYSTEM]: 'System',
  };
  return labels[type] || 'Unknown';
};

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const feedbackApi = createApi({
  reducerPath: "feedbackApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Ticket", "TicketStats"],
  endpoints: (builder) => ({
    // Get all tickets with filtering and pagination
    getTickets: builder.query({
      query: (params = {}) => {
        const page = typeof params === 'number' ? params : (params.page || 0);
        const queryParams = {
          page: page.toString(),
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.type !== undefined && { type: params.type }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.priority !== undefined && { priority: params.priority }),
          ...(params.category !== undefined && { category: params.category }),
          ...(params.tenantId && { tenantId: params.tenantId }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        };

        console.log('🔍 Ticket API Query Params:', queryParams);

        return {
          url: '/ticket/list',
          method: 'GET',
          params: queryParams,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Ticket API Response:', response);

        if (!response) {
          return {
            value: [],
            totalCount: 0,
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: response.value || [],
          totalCount: response.value?.length || 0,
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Ticket API Error:', response);
        return response;
      },
      providesTags: (result) =>
        result?.value
          ? [
              ...result.value.map(({ id }) => ({ type: "Ticket", id })),
              { type: "Ticket", id: "LIST" },
            ]
          : [{ type: "Ticket", id: "LIST" }],
    }),

    // Get single ticket by ID
    getTicketById: builder.query({
      query: (id) => ({
        url: `/ticket/${id}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        console.log('✅ Ticket Detail API Response:', response);
        const value = response?.value;
        if (!value) return { value: null };

        // Map backend detail to UI-friendly shape
        const mapped = {
          id: value.id,
          ticketNumber: value.ticketNumber,
          type: value.type,
          subject: value.subject,
          description: value.description,
          isAnonymous: value.isAnonymous,
          userName: value.userName,
          tenantName: value.tenantName,
          priority: value.priority,
          status: value.status,
          category: value.category,
          assignedToTenantId: value.assignedToTenantId,
          assignedToTenantName: value.assignedToTenantName,
          createdDate: value.createdDate || value.createdAt,
          resolvedDate: value.resolvedAt || value.resolvedDate,
          // Map messages to conversations used by UI
          conversations: Array.isArray(value.messages)
            ? value.messages.map(m => ({
                id: m.id,
                message: m.message,
                createdDate: m.createdDate,
                type: m.isSystemMessage
                  ? RESPONSE_TYPES.SYSTEM
                  : (m.isFromAdmin ? RESPONSE_TYPES.ADMIN : RESPONSE_TYPES.USER),
                isInternal: false, // backend doesn't expose internal marker here
                authorName: m.senderName || (m.isFromAdmin ? 'Admin' : 'User'),
                isRead: m.isRead,
              }))
            : [],
          attachments: Array.isArray(value.attachments)
            ? value.attachments.map(a => ({
                id: a.id,
                name: a.fileName,
                url: a.fileUrl,
                preview: a.fileUrl,
                type: a.fileType,
                size: a.fileSize,
                createdDate: a.createdDate,
                uploadedByAdmin: a.uploadedByAdmin,
              }))
            : [],
          history: value.history || [],
        };

        return { value: mapped, isSuccess: response?.isSuccess, status: response?.status };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Detail API Error:', response);
        return response;
      },
      providesTags: (result, error, id) => [{ type: "Ticket", id }],
    }),

    // Create new ticket
    createTicket: builder.mutation({
      query: (ticketData) => {
        console.log('🔥 Ticket Create API - Request Data:', ticketData);
        return {
          url: '/ticket',
          method: 'POST',
          body: ticketData,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Ticket Create API Response:', response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Create API Error:', response);
        return response;
      },
      invalidatesTags: ["Ticket", "TicketStats"],
    }),

    // Update ticket (PUT /ticket)
    updateTicket: builder.mutation({
      query: ({ id, type, priority, status, category }) => {
        const payload = {
          ticketId: id,
          ...(type !== undefined && { type: parseInt(type, 10) }),
          ...(priority !== undefined && { priority: parseInt(priority, 10) }),
          ...(status !== undefined && { status: parseInt(status, 10) }),
          ...(category !== undefined && { category: parseInt(category, 10) }),
        };
        console.log('🔥 Ticket Update API - Request Data:', payload);
        return {
          url: `/ticket`,
          method: 'PUT',
          body: payload,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Ticket Update API Response:', response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Update API Error:', response);
        return response;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Ticket", id },
        "Ticket",
        "TicketStats",
      ],
    }),

    // Delete ticket
    deleteTicket: builder.mutation({
      query: (id) => {
        console.log('🔥 Ticket Delete API - ID:', id);
        return {
          url: `/ticket/${id}`,
          method: 'DELETE',
        };
      },
      transformResponse: (response, meta) => {
        console.log('✅ Ticket Delete API Response:', response);
        
        // Handle 204 No Content
        if (meta?.response?.status === 204 || meta?.response?.status === HTTP_STATUS.NO_CONTENT) {
          return {
            isSuccess: true,
            status: 0,
            successMessage: "Ticket deleted successfully",
          };
        }

        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Delete API Error:', response);
        return response;
      },
      invalidatesTags: ["Ticket", "TicketStats"],
    }),

    // Get ticket statistics
    getTicketStats: builder.query({
      query: (tenantId) => ({
        url: '/ticket/statistics',
        method: 'GET',
        params: tenantId ? { tenantId } : {},
      }),
      transformResponse: (response) => {
        console.log('✅ Ticket Stats API Response:', response);
        return response?.value || {};
      },
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Stats API Error:', response);
        return response;
      },
      providesTags: ["TicketStats"],
    }),

    // Add conversation response to a ticket
    addConversationResponse: builder.mutation({
      query: ({ feedbackId, ticketId, message, type, isInternal = false, authorName, authorEmail }) => {
        const id = ticketId || feedbackId;
        return {
          url: '/ticket/response',
          method: 'POST',
          body: {
            ticketId: id,
            message,
            type,
            isInternal,
            authorName,
            authorEmail,
          },
        };
      },
      transformResponse: (response) => response,
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Add Conversation API Error:', response);
        return response;
      },
      invalidatesTags: (result, error, { feedbackId, ticketId }) => [
        { type: 'Ticket', id: ticketId || feedbackId },
        'Ticket',
      ],
    }),

    // Add simple message to ticket
    addTicketMessage: builder.mutation({
      query: ({ ticketId, message }) => ({
        url: '/ticket/add-message',
        method: 'POST',
        body: { ticketId, message },
      }),
      transformResponse: (response) => response,
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Add Message API Error:', response);
        return response;
      },
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'Ticket', id: ticketId },
        'Ticket',
      ],
    }),

    // Update ticket status via same PUT /ticket endpoint
    updateFeedbackStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: '/ticket',
        method: 'PUT',
        body: { ticketId: id, status: parseInt(status, 10) },
      }),
      transformResponse: (response) => response,
      transformErrorResponse: (response) => {
        console.error('❌ Ticket Status Update API Error:', response);
        return response;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Ticket', id },
        'Ticket',
        'TicketStats',
      ],
    }),
  }),
});

export const {
  useGetTicketsQuery: useGetFeedbacksQuery,
  useGetTicketByIdQuery: useGetFeedbackByIdQuery,
  useCreateTicketMutation: useCreateFeedbackMutation,
  useUpdateTicketMutation: useUpdateFeedbackMutation,
  useDeleteTicketMutation: useDeleteFeedbackMutation,
  useGetTicketStatsQuery: useGetFeedbackStatsQuery,
  useAddConversationResponseMutation,
  useUpdateFeedbackStatusMutation,
  useAddTicketMessageMutation,
} = feedbackApi;

// Legacy exports for backward compatibility
export const FEEDBACK_TYPES = TICKET_TYPES;
export const FEEDBACK_STATUS = TICKET_STATUS;
export const FEEDBACK_PRIORITY = TICKET_PRIORITY;
export const FEEDBACK_CATEGORIES = TICKET_CATEGORIES;
export const getFeedbackTypeLabel = getTicketTypeLabel;
export const getFeedbackStatusLabel = getTicketStatusLabel;
export const getFeedbackPriorityLabel = getTicketPriorityLabel;
export const getFeedbackCategoryLabel = getTicketCategoryLabel;
export const useGetFeedbacksByTenantQuery = useGetFeedbacksQuery;
